import Field from "@/components/Field";
import RecordView from "@/components/record/RecordView";
import TaxStatusSelect from "@/components/TaxStatusSelect";

import { PayrollDepartmentBasedInspection } from "@/types/payrollDepartmentBasedInspection";
import { UseQueryResult } from "@tanstack/react-query";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { useValueRenderer } from "../../../hooks/useValueRenderer";
import { TextareaWithLimitInfo } from "@/components/TextareaWithLimitInfo";
import { formatNumber } from "@/lib/utils/formatters";
import { Hint } from "@/components/ui/hint";
import { NumberInput } from "@/components/ui/NumberInput";

interface PayrollInspectionDepartmentBasedSectionsProps {
  entryQuery: UseQueryResult<PayrollDepartmentBasedInspection>;
}

export const PayrollInspectionDepartmentBasedSections = memo(
  ({ entryQuery }: PayrollInspectionDepartmentBasedSectionsProps) => {
    const { t } = useTranslation();
    const entry = entryQuery.data;

    const renderValue = useValueRenderer();

    return (
      <>
        <RecordView
          query={entryQuery}
          translationKey="payrolls.fieldGroups.basic"
          header={() => t("payrolls.fieldGroups.basic")}
          isOpen={true}
        >
          <RecordView.Field
            name="period"
            label={t("payrolls.fields.period")}
            renderer={() => renderValue(entry?.period)}
          />

          <RecordView.Field
            name="department"
            label={t("payrolls.fields.department")}
            renderer={() => renderValue(entry?.department)}
          />
          <RecordView.Field
            name="store"
            label={t("payrolls.fields.store")}
            renderer={() => renderValue(entry?.store)}
          />
        </RecordView>

        <RecordView
          query={entryQuery}
          translationKey="payrolls.fieldGroups.contractor"
          header={() => t("payrolls.fieldGroups.contractor")}
          isOpen={true}
        >
          <RecordView.Field
            name="contractor"
            label={t("payrolls.fields.contractor")}
            renderer={() => renderValue(entry?.contractor)}
          />
          <RecordView.Field
            name="personal_id_number"
            label={t("payrolls.fields.personal_id_number")}
            renderer={() => renderValue(entry?.personal_id_number)}
          />
          <RecordView.Field
            name="agreement_company"
            label={t("payrolls.fields.agreement_company")}
            renderer={() => renderValue(entry?.agreement_company)}
          />
          <RecordView.Field
            name="agreement_status_period"
            label={t("payrolls.fields.agreement_status_period")}
            renderer={() =>
              renderValue(entry?.agreement_status_period, "agreementStatuses")
            }
          />
          <RecordView.Field
            name="tax_status_period"
            label={t("payrolls.fields.tax_status_period")}
            renderer={() => renderValue(entry?.tax_status_period, "taxStatus")}
          />
          <RecordView.Field
            name="tax_status_current"
            label={t("payrolls.fields.tax_status_current")}
            renderer={() => renderValue(entry?.tax_status_current, "taxStatus")}
          />
          <Field
            label={t("payrolls.fields.tax_status_corrected")}
            name="tax_status_corrected"
            render={({ field }) => <TaxStatusSelect {...field} />}
          />
          <Hint>{t("payrolls.hints.tax_status_corrected")}</Hint>
        </RecordView>

        <RecordView
          query={entryQuery}
          translationKey="payrolls.fieldGroups.settlement"
          header={() => t("payrolls.fieldGroups.settlement")}
          isOpen={true}
        >
          <RecordView.Field
            name="hourly_rate"
            label={t("payrolls.fields.hourly_rate")}
            renderer={() => formatNumber(entry?.hourly_rate)}
          />
          <RecordView.Field
            name="hours_worked"
            label={t("payrolls.fields.hours_worked")}
            renderer={() => formatNumber(entry?.hours_worked)}
          />
          <Field
            name="hours_worked_corrected"
            label={t("payrolls.fields.hours_worked_corrected")}
            render={({ field }) => <NumberInput {...field} />}
          />
          <RecordView.Field
            name="hours_worked_final"
            label={t("payrolls.fields.hours_worked_final")}
            renderer={() => formatNumber(entry?.hours_worked_final)}
          />
        </RecordView>

        <RecordView
          query={entryQuery}
          translationKey="payrolls.fieldGroups.financial"
          header={() => t("payrolls.fieldGroups.financial")}
          isOpen={true}
        >
          <RecordView.Field
            name="base_amount_to_pay"
            label={t("payrolls.fields.base_amount_to_pay")}
            renderer={() => formatNumber(entry?.base_amount_to_pay)}
          />
          <Field
            name="deduction"
            label={t("payrolls.fields.deduction")}
            render={({ field }) => <NumberInput {...field} />}
          />
          <Field
            name="penalty"
            label={t("payrolls.fields.penalty")}
            render={({ field }) => <NumberInput {...field} />}
          />
          <Field
            name="cash_shortage"
            label={t("payrolls.fields.cash_shortage")}
            render={({ field }) => <NumberInput {...field} />}
          />
          <Field
            name="addition"
            label={t("payrolls.fields.addition")}
            render={({ field }) => <NumberInput {...field} />}
          />
          <Field
            name="bonus"
            label={t("payrolls.fields.bonus")}
            render={({ field }) => <NumberInput {...field} />}
          />
          <RecordView.Field
            name="amount_to_pay"
            label={t("payrolls.fields.amount_to_pay")}
            renderer={() => formatNumber(entry?.amount_to_pay)}
          />
        </RecordView>

        <RecordView
          query={entryQuery}
          translationKey="payrolls.fieldGroups.structure"
          header={() => t("payrolls.fieldGroups.structure")}
          isOpen={true}
        >
          <RecordView.Field
            name="regional_director"
            label={t("payrolls.fields.regional_director")}
            renderer={() => renderValue(entry?.regional_director)}
          />
          <RecordView.Field
            name="regional_representative"
            label={t("payrolls.fields.regional_representative")}
            renderer={() => renderValue(entry?.regional_representative)}
          />
        </RecordView>

        <RecordView
          query={entryQuery}
          translationKey="payrolls.fieldGroups.comments"
          header={() => t("payrolls.fieldGroups.comments")}
          isOpen={true}
        >
          <RecordView.Field
            name="permanent_comment"
            label={t("payrolls.fields.permanent_comment")}
            renderer={() => renderValue(entry?.permanent_comment)}
          />
          <Field
            name="comment"
            label={t("payrolls.fields.comment_inspection")}
            render={({ field }) => (
              <TextareaWithLimitInfo
                {...field}
                placeholder={t("payrolls.placeholders.comment")}
                maxLength={250}
              />
            )}
          />
        </RecordView>
      </>
    );
  },
);

PayrollInspectionDepartmentBasedSections.displayName =
  "PayrollInspectionDetail";
